import { Registerable } from '@baserow/modules/core/registry'

/**
 */
export class PermissionManagerType extends Registerable {
  /**
   * This method will be called by the `$hasPermission` plugin for each entry of the
   * loaded permission object. This method should return `true` if the permission is
   * granted, `false` if it is disallowed and `null` if the permission manager doesn't
   * have the responsibility to answer in this context.
   *
   * @param {*} permissions the object generated by the backend permission manager with
   *   the same name.
   * @param {string} operation the operation name.
   * @param {*} context the context object on which the operation applies.
   * @param {*} workspaceId the workspaceId the permission is being requested in if
   *   there is a workspace involved, if not then this will be null.
   */
  hasPermission(permissions, operation, context, workspaceId) {}

  /**
   * The order value used to sort admin types in the sidebar menu.
   */
  getOrder() {
    return 0
  }

  /**
   * Translation mappings for all the roles that have been added by your
   * permission manager
   */
  getRolesTranslations() {
    return {}
  }
}

export class CorePermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'core'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    if (permissions.includes(operation)) {
      return true
    }
  }
}

export class StaffPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'staff'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    if (permissions.staff_only_operations.includes(operation)) {
      return permissions.is_staff
    }
  }
}

export class WorkspaceMemberPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'member'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    return permissions
  }
}

export class BasicPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'basic'
  }

  getRolesTranslations() {
    const { $i18n: i18n } = this.app

    return {
      ADMIN: {
        name: i18n.t('permission.admin'),
        description: i18n.t('permission.adminDescription'),
      },
      MEMBER: {
        name: i18n.t('permission.member'),
        description: i18n.t('permission.memberDescription'),
      },
    }
  }

  hasPermission(permissions, operation, context, workspaceId) {
    // Check if this is an RBAC operation - if so, don't handle it
    // Let the RBAC permission manager handle it
    if (VokalisRbacPermissionManagerType.RBAC_OPERATIONS.includes(operation)) {
      // This is an RBAC operation - don't handle it here
      // Return null to let the RBAC manager handle it
      return null
    }

    // Is it an admin only operation?
    if (permissions.admin_only_operations.includes(operation)) {
      // yes, so it should be an admin of the workspace
      if (permissions.is_admin) {
        // It is!
        return true
      }
    } else {
      // It's a member and it's a non admin only operation.
      return true
    }
    // None of the above applied
    return false
  }
}

export class StaffOnlySettingOperationPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'setting_operation'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    // Fetch isStaff from the auth store, so we can reactively update the permission.
    const { $store } = this.app
    const isStaff = $store.getters['auth/isStaff']

    // Check if the operation is in either array of operations.
    if (
      permissions.always_allowed_operations.includes(operation) ||
      permissions.staff_only_operations.includes(operation)
    ) {
      // Permission is...
      // A) allowed if the Setting it relates to is set to "everyone", or
      // B) allowed if, regardless of the Setting, the actor isStaff.
      return (
        permissions.always_allowed_operations.includes(operation) ||
        (permissions.staff_only_operations.includes(operation) && isStaff)
      )
    }
  }
}

export class AllowIfTemplateOperationPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'allow_if_template'
  }

  hasPermission(permissions, operation, context, workspaceId) {
    if (permissions.workspace_template_ids.includes(workspaceId)) {
      if (permissions.allowed_operations_on_templates.includes(operation)) {
        return true
      }
    }
    // Workspace ID `0` can be used to fake workspace objects, like for example
    // during the onboarding.
    // @TODO this is not the nicest place to allow this. decide whether we want to
    // implement it this way.
    if (workspaceId === 0 && context?._?.is_onboarding) {
      return true
    }
  }
}

export class VokalisRbacPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'vokalis_rbac'
  }

  // Map of operations that RBAC handles - must match backend OPERATION_TO_ACTION_MAP
  static RBAC_OPERATIONS = [
    // Workspace operations
    'workspace.read',
    'workspace.manage',
    'workspace.update',
    'workspace.delete',
    'workspace.restore',
    'workspace.create_application',
    'workspace.order_applications',
    'workspace.create_invitation',
    'workspace.list_workspace_users',
    'workspace.list_applications',
    'workspace.list_invitations',
    'workspace_user.update',
    'workspace_user.delete',
    // Application/Database operations
    'application.read',
    'application.update',
    'application.delete',
    'application.restore',
    'application.duplicate',
    'application.create_snapshot',
    'application.read_trash',
    'database.read',
    'database.update',
    'database.delete',
    'database.create_table',
    'database.list_tables',
    'database.order_tables',
    // Table operations
    'database.table.read',
    'database.table.update',
    'database.table.delete',
    'database.table.duplicate',
    'database.table.restore',
    'database.table.run_export',
    'database.table.listen_to_all',
    'database.table.list_rows',
    'database.table.list_row_names',
    // Row operations
    'database.table.read_row',
    'database.table.read_adjacent_row',
    'database.table.move_row',
    'database.table.create_row',
    'database.table.update_row',
    'database.table.delete_row',
    'database.table.restore_row',
    'database.table.read_row_history',
    'database.table.import_rows',
    // Field operations
    'database.table.create_field',
    'database.table.list_fields',
    'database.table.field.read',
    'database.table.field.update',
    'database.table.field.delete',
    'database.table.field.delete_related_link_row_field',
    'database.table.field.restore',
    'database.table.field.duplicate',
    'database.table.field.write_values',
    'database.table.field.submit_anonymous_values',
    // View operations
    'database.table.view.read',
    'database.table.view.read_row',
    'database.table.view.create_row',
    'database.table.view.update_row',
    'database.table.view.delete_row',
    'database.table.create_view',
    'database.table.create_public_view',
    'database.table.create_and_use_personal_view',
    'database.table.list_views',
    'database.table.read_view_order',
    'database.table.order_views',
    'database.table.view.read_field_options',
    'database.table.view.update_field_options',
    'database.table.view.update',
    'database.table.view.update_slug',
    'database.table.view.update_public',
    'database.table.view.can_receive_notification_on_submit_form_view',
    'database.table.view.delete',
    'database.table.view.restore',
    'database.table.view.duplicate',
    // View filter operations
    'database.table.view.create_filter',
    'database.table.view.list_filter',
    'database.table.view.filter.read',
    'database.table.view.filter.update',
    'database.table.view.filter.delete',
    // View filter group operations
    'database.table.view.create_filter_group',
    'database.table.view.filter_group.read',
    'database.table.view.filter_group.update',
    'database.table.view.filter_group.delete',
    // View sort operations
    'database.table.view.create_sort',
    'database.table.view.list_sort',
    'database.table.view.sort.read',
    'database.table.view.sort.update',
    'database.table.view.sort.delete',
    // View group by operations
    'database.table.view.create_group_by',
    'database.table.view.list_group_bys',
    'database.table.view.group_by.read',
    'database.table.view.group_by.update',
    'database.table.view.group_by.delete',
    // View decoration operations
    'database.table.view.create_decoration',
    'database.table.view.list_decoration',
    'database.table.view.decoration.read',
    'database.table.view.decoration.update',
    'database.table.view.decoration.delete',
    // View aggregation operations
    'database.table.view.list_aggregations',
    'database.table.view.read_aggregation',
    // Webhook operations
    'database.table.list_webhooks',
    'database.table.create_webhook',
    'database.table.webhook.read',
    'database.table.webhook.update',
    'database.table.webhook.delete',
    'database.table.webhook.test_trigger',
    // Field rules operations
    'database.table.field_rules.read_field_rules',
    'database.table.field_rules.set_field_rules',
  ]

  hasPermission(permissions, operation, context, workspaceId) {
    // Check if this operation is handled by RBAC
    const isRbacOperation = VokalisRbacPermissionManagerType.RBAC_OPERATIONS.includes(operation)
    
    if (!isRbacOperation) {
      // Not an RBAC operation, let other permission managers handle it
      return null
    }

    // This is an RBAC operation
    if (!permissions || !permissions.vokalis_rbac) {
      // No RBAC permissions object - deny by default
      return false
    }

    const rbacPerms = permissions.vokalis_rbac
    
    // Map operation to resource type, resource ID, and action
    const operationMapping = this._getOperationMapping(operation, context)
    if (!operationMapping) {
      // Can't determine resource - deny by default
      return false
    }

    const { resourceType, resourceId, action } = operationMapping
    
    // Check permissions based on resource type
    if (resourceType === 'workspace') {
      if (rbacPerms.workspace && rbacPerms.workspace[action] !== undefined) {
        return rbacPerms.workspace[action] === true
      }
    } else if (resourceType === 'database') {
      if (rbacPerms.databases && resourceId && rbacPerms.databases[resourceId]) {
        const dbPerms = rbacPerms.databases[resourceId]
        if (dbPerms[action] !== undefined) {
          return dbPerms[action] === true
        }
      }
    } else if (resourceType === 'application') {
      // Prefer dedicated application permissions when available.
      if (
        rbacPerms.applications &&
        resourceId &&
        rbacPerms.applications[resourceId]
      ) {
        const appPerms = rbacPerms.applications[resourceId]
        if (appPerms[action] !== undefined) {
          return appPerms[action] === true
        }
      }

      // Backward compatibility: some payloads still expose app permissions
      // under `databases` keyed by application id.
      if (rbacPerms.databases && resourceId && rbacPerms.databases[resourceId]) {
        const dbPerms = rbacPerms.databases[resourceId]
        if (dbPerms[action] !== undefined) {
          return dbPerms[action] === true
        }
      }

      // Final fallback for workspace admins when app-specific grants are absent.
      if (rbacPerms.workspace && rbacPerms.workspace[action] !== undefined) {
        return rbacPerms.workspace[action] === true
      }
    } else if (resourceType === 'table') {
      if (rbacPerms.tables && resourceId) {
        // Helper function to safely get permission value from Vue reactive object
        const getPermissionValue = (perms, actionName) => {
          if (!perms || typeof perms !== 'object') {
            return undefined
          }
          // Handle Vue reactive objects - access the value directly
          const value = perms[actionName]
          // Vue reactive getters return the value, but we need to ensure it's a boolean
          if (value === true || value === false) {
            return value === true
          }
          return undefined
        }
        
        // Try exact string match first
        const tableIdStr = String(resourceId)
        const tableIdNum = Number(resourceId)
        
        let tablePerms = null
        if (rbacPerms.tables[tableIdStr]) {
          tablePerms = rbacPerms.tables[tableIdStr]
        } else if (!isNaN(tableIdNum) && rbacPerms.tables[tableIdNum]) {
          tablePerms = rbacPerms.tables[tableIdNum]
        } else {
          // Try all keys to find matching table ID
          const tableKeys = Object.keys(rbacPerms.tables)
          for (const key of tableKeys) {
            if (String(key) === tableIdStr || Number(key) === tableIdNum) {
              tablePerms = rbacPerms.tables[key]
              break
            }
          }
        }
        
        if (tablePerms) {
          const permValue = getPermissionValue(tablePerms, action)
          if (permValue !== undefined) {
            return permValue
          }
        }
      }
    }
    
    return false
  }

  _getOperationMapping(operation, context) {
    // Map operation name to action - must match backend OPERATION_TO_ACTION_MAP
    const operationToAction = {
      // Workspace operations
      'workspace.read': 'read',
      'workspace.manage': 'manage',
      'workspace.update': 'update',
      'workspace.delete': 'delete',
      'workspace.restore': 'update',
      'workspace.create_application': 'create',
      'workspace.order_applications': 'update',
      'workspace.create_invitation': 'manage',
      'workspace.list_workspace_users': 'read',
      'workspace.list_applications': 'read',
      'workspace.list_invitations': 'manage',
      'workspace_user.update': 'manage',
      'workspace_user.delete': 'manage',
      // Application/Database operations
      'application.read': 'read',
      'application.update': 'update',
      'application.delete': 'delete',
      'application.restore': 'update',
      'application.duplicate': 'create',
      'application.create_snapshot': 'manage',
      'application.read_trash': 'read',
      'database.read': 'read',
      'database.update': 'update',
      'database.delete': 'delete',
      'database.create_table': 'create',
      'database.list_tables': 'read',
      'database.order_tables': 'update',
      // Table operations
      'database.table.read': 'read',
      'database.table.update': 'update',
      'database.table.delete': 'delete',
      'database.table.duplicate': 'create',
      'database.table.restore': 'update',
      'database.table.run_export': 'read',
      'database.table.listen_to_all': 'read',
      'database.table.list_rows': 'read',
      'database.table.list_row_names': 'read',
      // Row operations
      'database.table.read_row': 'read',
      'database.table.read_adjacent_row': 'read',
      'database.table.move_row': 'update',
      'database.table.create_row': 'create',
      'database.table.update_row': 'update',
      'database.table.delete_row': 'delete',
      'database.table.restore_row': 'update',
      'database.table.read_row_history': 'read',
      'database.table.import_rows': 'create',
      // Field operations
      'database.table.create_field': 'update',
      'database.table.list_fields': 'read',
      'database.table.field.read': 'read',
      'database.table.field.update': 'update',
      'database.table.field.delete': 'update',
      'database.table.field.delete_related_link_row_field': 'update',
      'database.table.field.restore': 'update',
      'database.table.field.duplicate': 'update',
      'database.table.field.write_values': 'update',
      'database.table.field.submit_anonymous_values': 'create',
      // View operations
      'database.table.view.read': 'read',
      'database.table.view.read_row': 'read',
      'database.table.view.create_row': 'create',
      'database.table.view.update_row': 'update',
      'database.table.view.delete_row': 'delete',
      'database.table.create_view': 'update',
      'database.table.create_public_view': 'update',
      'database.table.create_and_use_personal_view': 'update',
      'database.table.list_views': 'read',
      'database.table.read_view_order': 'read',
      'database.table.order_views': 'update',
      'database.table.view.read_field_options': 'read',
      'database.table.view.update_field_options': 'update',
      'database.table.view.update': 'update',
      'database.table.view.update_slug': 'update',
      'database.table.view.update_public': 'update',
      'database.table.view.can_receive_notification_on_submit_form_view': 'read',
      'database.table.view.delete': 'update',
      'database.table.view.restore': 'update',
      'database.table.view.duplicate': 'update',
      // View filter operations
      'database.table.view.create_filter': 'update',
      'database.table.view.list_filter': 'read',
      'database.table.view.filter.read': 'read',
      'database.table.view.filter.update': 'update',
      'database.table.view.filter.delete': 'update',
      // View filter group operations
      'database.table.view.create_filter_group': 'update',
      'database.table.view.filter_group.read': 'read',
      'database.table.view.filter_group.update': 'update',
      'database.table.view.filter_group.delete': 'update',
      // View sort operations
      'database.table.view.create_sort': 'update',
      'database.table.view.list_sort': 'read',
      'database.table.view.sort.read': 'read',
      'database.table.view.sort.update': 'update',
      'database.table.view.sort.delete': 'update',
      // View group by operations
      'database.table.view.create_group_by': 'update',
      'database.table.view.list_group_bys': 'read',
      'database.table.view.group_by.read': 'read',
      'database.table.view.group_by.update': 'update',
      'database.table.view.group_by.delete': 'update',
      // View decoration operations
      'database.table.view.create_decoration': 'update',
      'database.table.view.list_decoration': 'read',
      'database.table.view.decoration.read': 'read',
      'database.table.view.decoration.update': 'update',
      'database.table.view.decoration.delete': 'update',
      // View aggregation operations
      'database.table.view.list_aggregations': 'read',
      'database.table.view.read_aggregation': 'read',
      // Webhook operations
      'database.table.list_webhooks': 'read',
      'database.table.create_webhook': 'update',
      'database.table.webhook.read': 'read',
      'database.table.webhook.update': 'update',
      'database.table.webhook.delete': 'update',
      'database.table.webhook.test_trigger': 'update',
      // Field rules operations
      'database.table.field_rules.read_field_rules': 'read',
      'database.table.field_rules.set_field_rules': 'update',
    }

    const action = operationToAction[operation]
    if (!action) {
      return null
    }

    // Determine resource type and ID from context
    if (!context) {
      // No context - assume workspace operation
      if (operation.startsWith('workspace.')) {
        return { resourceType: 'workspace', resourceId: null, action }
      }
      return null
    }

    // Handle table operations
    if (operation.startsWith('database.table.') || operation.startsWith('database.table.view.')) {
      // Context could be a table, view, field, or database
      let tableId = null
      
      // Check if context is a field - extract table_id from field
      if (context.table_id) {
        tableId = String(context.table_id)
      }
      // Check if context has a table property (view or field)
      else if (context.table && context.table.id) {
        tableId = String(context.table.id)
      }
      // Check if context has a database property (table) - use context.id as table ID
      else if (context.database && context.database.id && context.id) {
        tableId = String(context.id)
      }
      // Check if context.id exists (table object)
      else if (context.id) {
        tableId = String(context.id)
      }
      
      if (tableId) {
        return { resourceType: 'table', resourceId: tableId, action }
      }
      return null
    }

    // Handle application operations
    if (operation.startsWith('application.')) {
      if (context.id) {
        return {
          resourceType: 'application',
          resourceId: String(context.id),
          action,
        }
      }
      return null
    }

    // Handle database operations
    if (operation.startsWith('database.')) {
      // For database.create_table, context is the database
      if (operation === 'database.create_table' || operation === 'database.list_tables') {
        // Context could be a database or a table (if called from table context)
        if (context.database && context.database.id) {
          return { resourceType: 'database', resourceId: String(context.database.id), action }
        }
        if (context.id) {
          return { resourceType: 'database', resourceId: String(context.id), action }
        }
        return null
      }
      // For other database operations, context is the database
      if (context.id) {
        // Check if context has database property (table context)
        if (context.database && context.database.id) {
          return { resourceType: 'database', resourceId: String(context.database.id), action }
        }
        // Assume context is the database
        return { resourceType: 'database', resourceId: String(context.id), action }
      }
      return null
    }

    // Handle workspace operations
    if (operation.startsWith('workspace.')) {
      return { resourceType: 'workspace', resourceId: null, action }
    }

    return null
  }

  getRolesTranslations() {
    // RBAC roles are managed dynamically, so we don't have static translations
    return {}
  }
}

/**
 * Carries `workspace_navigation` payload from GET /workspaces/:id/permissions/.
 * Capability checks use $canNavCapability instead of operation names.
 */
export class WorkspaceNavigationPermissionManagerType extends PermissionManagerType {
  static getType() {
    return 'workspace_navigation'
  }

  hasPermission() {
    return null
  }
}
